import { useEffect, type ReactNode } from 'react';
import { Box } from '@chakra-ui/react';
import { useUnderwayStore } from './useUnderwayStore';

export type UnderwayHoverPauseBoundaryProps = {
  /** Only wire the hover-pause while automation rows are hosted in the popup. */
  active: boolean;
  children: ReactNode;
};

/**
 * Hover boundary around the WHOLE popup (header included): pointer anywhere
 * inside pauses ALL pending automations via the store flag each row reads.
 */
export const UnderwayHoverPauseBoundary = ({
  active,
  children,
}: UnderwayHoverPauseBoundaryProps) => {
  const setWidgetHovered = useUnderwayStore((s) => s.setWidgetHovered);

  // The popup can disappear mid-hover (no mouseleave fires) — reset on
  // deactivate/unmount or the stuck flag would pause everything forever.
  useEffect(() => {
    if (!active) setWidgetHovered(false);
    return () => setWidgetHovered(false);
  }, [active, setWidgetHovered]);

  if (!active) return <>{children}</>;

  return (
    <Box
      onMouseEnter={() => setWidgetHovered(true)}
      onMouseLeave={() => setWidgetHovered(false)}
      data-test-id="automation-hover-pause-boundary"
    >
      {children}
    </Box>
  );
};
